// Refactor 02/2026 — extrahiert aus AppointmentsPage.js.
// Render-Komponenten für die einzelnen Sub-Slots eines Termins:
//   - ParticipantSlot     → belegter Sub-Slot (mit Tooltip & DnD)
//   - MindbodyBlockedSlot → durch MB-Direktbuchung gesperrter Platz
//   - EmptyParticipantSlot → leerer Sub-Slot (Klick = Teilnehmer-Hinzufügen,
//                                              Drop = Teilnehmer einsortieren)
import React from 'react';
import { AlertCircle, FileSignature, Phone, Plus, Snowflake } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import ReservationCountdownBadge from '@/components/ReservationCountdownBadge';
import MindbodyPushBadge from '@/components/MindbodyPushBadge';
import { BookingSourceIcon, getSwissHM } from './calendarUtils';
import { longPressHandlers } from './touchLongPress';
import { getAppointmentTextColor } from '@/utils/appointmentDisplay';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─────────────────────────────────────────────────────────────────────
// Belegter Sub-Slot: Name + Service-Badge + Tooltip + DnD
// ─────────────────────────────────────────────────────────────────────
// Kleiner runder Foto-Avatar im Kalender — NUR wenn ein echtes Profilfoto
// vorhanden ist. Ohne Foto wird NICHTS gerendert (kein Kürzel-Kreis), damit
// wie früher der Name im Vordergrund steht.
const SlotAvatar = ({ photoUrl, size = 20, className = '' }) => {
  if (!photoUrl) return null;
  const src = `${process.env.REACT_APP_BACKEND_URL}/api${photoUrl}`;
  return (
    <span
      className={`group relative inline-flex items-center justify-center rounded-full overflow-visible shrink-0 align-middle ${className}`}
      style={{ width: size, height: size }}
      data-testid="slot-avatar"
    >
      <img src={src} alt="" className="w-full h-full object-cover rounded-full" />
      {/* Hover: Bild gross anzeigen (nur Desktop) */}
      <span className="pointer-events-none absolute z-50 left-1/2 -translate-x-1/2 bottom-full mb-1 hidden group-hover:block">
        <img src={src} alt="" className="w-40 h-40 object-cover rounded-xl shadow-2xl border-2 border-white" />
      </span>
    </span>
  );
};

export const ParticipantSlot = ({
  apt,
  slotIdx,
  maxParticipants,
  name,
  customerId,
  phone,
  suitSize,
  blockFields = [],
  hasFailedPayment,
  consentMissing,
  photoUrl,
  startTimeStr,
  endTimeStr,
  topPosition,
  leftPosition,
  widthPercentage,
  height,
  durationMinutes,
  isEmsOrFlex,
  anamneseStatus,
  appointmentColor,
  pushRelevant = false,
  onParticipantDragStart,
  onDragEnd,
  onRightClick,
  onMindbodyPushRetryDone,
  onResized,
  checkResizeConflict,
}) => {
  const navigate = useNavigate();
  // 10/2026 Feature "Dauer beim Ziehen ändern": Unterkante ziehen → End-Zeit
  // ändern (auf 15 Min eingerastet). Nur für echte Einzel-Termine.
  const [resizeDelta, setResizeDelta] = React.useState(null);
  const [dragDisabled, setDragDisabled] = React.useState(false);
  const canResize =
    !apt._isReservation &&
    apt.status !== 'cancelled' &&
    apt.status !== 'no_show' &&
    maxParticipants === 1 &&
    durationMinutes > 0;
  const pxPerMin = durationMinutes > 0 ? height / durationMinutes : 2;
  const resizing = resizeDelta !== null;
  const liveHeight = resizing
    ? Math.max(pxPerMin * 15, height + resizeDelta)
    : height;
  // Live-Vorschau der Dauer/End-Zeit beim Ziehen der Unterkante.
  const liveSnappedMin = resizing
    ? Math.max(15, Math.round((height + resizeDelta) / pxPerMin / 15) * 15)
    : null;
  const liveEndLabel =
    liveSnappedMin != null
      ? (() => {
          const sH = getSwissHM(apt.start_time);
          const startTotal = sH.hour * 60 + sH.minute;
          const endTotal = startTotal + liveSnappedMin;
          const p = (n) => String(n).padStart(2, '0');
          return `${p(Math.floor(startTotal / 60) % 24)}:${p(startTotal % 60)}–${p(
            Math.floor(endTotal / 60) % 24
          )}:${p(endTotal % 60)} (${liveSnappedMin}′)`;
        })()
      : null;
  // Live-Konflikt: wächst der Termin in einen belegten Bereich? → rot anzeigen.
  const resizeNewEnd =
    liveSnappedMin != null
      ? new Date(new Date(apt.start_time).getTime() + liveSnappedMin * 60000)
      : null;
  const resizeConflict =
    resizing && resizeNewEnd && typeof checkResizeConflict === 'function'
      ? checkResizeConflict(apt, resizeNewEnd)
      : false;

  const startResize = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragDisabled(true);
    const _origEnd = apt.end_time;
    const startY = e.clientY;
    const onMove = (ev) => setResizeDelta(ev.clientY - startY);
    const onUp = async (ev) => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      // Phantom-Click unterdrücken: Browser feuert nach Mousedown+Mouseup auf
      // verschiedenen Elementen einen Click auf den gemeinsamen Vorfahren
      // (TD.drop-zone) → sonst öffnet sich ungewollt der "Slot öffnen"-Dialog.
      const swallow = (ce) => {
        ce.stopPropagation();
        ce.preventDefault();
      };
      document.addEventListener('click', swallow, { capture: true, once: true });
      setTimeout(
        () => document.removeEventListener('click', swallow, { capture: true }),
        300
      );
      setDragDisabled(false);
      const delta = ev.clientY - startY;
      setResizeDelta(null);
      const snapped = Math.max(
        15,
        Math.round((height + delta) / pxPerMin / 15) * 15
      );
      if (snapped === Math.round(durationMinutes)) return;
      const start = new Date(apt.start_time);
      const newEnd = new Date(start.getTime() + snapped * 60000);
      // Belegt-Warnung: Ziel-Bereich belegt → NICHT speichern.
      if (typeof checkResizeConflict === 'function' && checkResizeConflict(apt, newEnd)) {
        toast.error('Zielbereich belegt — Dauer nicht geändert');
        return;
      }
      try {
        await axios.put(`${API}/appointments/${apt.id}`, {
          end_time: newEnd.toISOString(),
        });
        // Schnell-Rückgängig: End-Zeit auf den Original-Wert zurücksetzen.
        toast.success(`Dauer: ${snapped} Min`, {
          action: {
            label: 'Rückgängig',
            onClick: async () => {
              try {
                await axios.put(`${API}/appointments/${apt.id}`, {
                  end_time: _origEnd,
                });
                toast.success('Dauer zurückgesetzt');
                onResized?.();
              } catch (err) {
                toast.error('Rückgängig fehlgeschlagen');
                onResized?.();
              }
            },
          },
          duration: 8000,
        });
        onResized?.();
      } catch (err) {
        toast.error(
          err.response?.data?.detail || 'Dauer konnte nicht geändert werden'
        );
        onResized?.();
      }
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const goToCustomer = (e) => {
    e.stopPropagation();
    if (apt._isReservation && customerId) navigate(`/customers?open=${customerId}`);
  };
  return (
  <Tooltip key={`tooltip-${apt.id}-${slotIdx}`} delayDuration={300}>
    <TooltipTrigger asChild>
      <div
        draggable={!apt._isReservation && !dragDisabled}
        onDragStart={(e) =>
          onParticipantDragStart(e, apt, customerId, name)
        }
        onDragEnd={onDragEnd}
        data-testid={`participant-slot-${apt.id}`}
        data-appointment-id={apt.id}
        className="absolute p-2 cursor-move flex flex-col items-center justify-center hover:opacity-90 transition-opacity"
        style={{
          top: `${topPosition}px`,
          left: `${leftPosition}%`,
          width: `${widthPercentage}%`,
          height: `${liveHeight}px`,
          backgroundColor: appointmentColor,
          zIndex: resizing ? 50 : 10,
          boxShadow: '0 4px 8px rgba(0,0,0,0.4)',
          borderTop: apt._isReservation
            ? '3px dashed #B45309'
            : '2px solid #888',
          // Iter 351h: Add-on-Termine (z.B. Kryosauna via Upsell) deutlich
          // markieren — lila Streifen links + Schneeflocken-Badge.
          borderLeft: apt.is_addon ? '4px solid #8B5CF6' : undefined,
          borderRight:
            slotIdx < maxParticipants - 1 ? '1px solid #888' : 'none',
          opacity: apt._isReservation ? 0.92 : 1,
          // Iter 351n: iOS-Callout/Textauswahl beim Long-Press unterdrücken
          WebkitTouchCallout: 'none',
          WebkitUserSelect: 'none',
          userSelect: 'none',
        }}
        title={
          apt._isReservation
            ? `Slot-Reservation – läuft ab am ${new Date(
                apt._expiresAt
              ).toLocaleString('de-CH')}`
            : undefined
        }
        onClick={(e) => {
          e.stopPropagation();
        }}
        onContextMenu={(e) => {
          e.stopPropagation();
          if (!apt._isReservation) {
            onRightClick(e, apt, customerId);
          }
        }}
        // Iter 351n: iPad/iPhone haben keinen Rechtsklick — langes Drücken
        // (500ms) öffnet dasselbe Kontextmenü (Bearbeiten/Stornieren/...).
        {...longPressHandlers(
          (pos) => {
            if (!apt._isReservation) {
              onRightClick(
                {
                  preventDefault() {},
                  stopPropagation() {},
                  clientX: pos.clientX,
                  clientY: pos.clientY,
                },
                apt,
                customerId
              );
            }
          },
          { stopPropagation: true }
        )}
      >
        {/* Iter 309: Reservation-Badge mit Live-Countdown */}
        {apt._isReservation && (
          <ReservationCountdownBadge expiresAt={apt._expiresAt} />
        )}
        {/* Iter 329d: Mindbody Push-Status (nur auf Slot 0) */}
        {slotIdx === 0 && !apt._isReservation && (
          <MindbodyPushBadge
            appointment={apt}
            compact={durationMinutes <= 15}
            pushRelevant={pushRelevant}
            onRetryDone={onMindbodyPushRetryDone}
          />
        )}
        {/* For short appointments (≤ 15 min), show info more compact */}
        {durationMinutes <= 15 ? (
          <div
            className="relative text-[9px] text-center px-0.5 leading-tight w-full h-full"
            style={{ color: getAppointmentTextColor(appointmentColor) }}
          >
            {/* Add-on indicator - top left corner (Iter 351h: Kryo-Icon) */}
            {apt.is_addon && (
              <div
                className="absolute -top-1 -left-1 z-10"
                data-testid={`addon-badge-${apt.id}`}
              >
                <span
                  className="w-4 h-4 bg-purple-600 text-white rounded-full flex items-center justify-center shadow-md border border-white"
                  title="Zusatzleistung (Add-on)"
                >
                  <Snowflake size={10} strokeWidth={2.5} />
                </span>
              </div>
            )}
            {/* Booking source indicator in background corner */}
            {apt.booking_source && (
              <div className="absolute top-0 right-0 opacity-40">
                <BookingSourceIcon source={apt.booking_source} />
              </div>
            )}
            <div className="font-semibold">
              {startTimeStr}-{endTimeStr}
            </div>
            <div className="flex items-center justify-center gap-0.5">
              <span className="truncate">{apt.service_name}{apt.selected_variant ? ` (${apt.selected_variant.replace(/\s*Training$/i, '').trim()})` : ''}</span>
              <span>|</span>
              {photoUrl && <SlotAvatar photoUrl={photoUrl} size={14} className="mr-0.5" />}
              {apt._isReservation ? (
                <span
                  className="font-semibold truncate underline decoration-dotted cursor-pointer hover:opacity-80"
                  onClick={goToCustomer}
                  title="Zum Kundenprofil"
                >
                  {name}
                </span>
              ) : (
                <span className="font-semibold truncate">{name}</span>
              )}
              {hasFailedPayment && (
                <span
                  className="inline-block w-2 h-2 rounded-full bg-red-600 ml-0.5 shrink-0"
                  title="Offene Rechnung – Karte nicht belastbar"
                  data-testid={`failed-payment-dot-${customerId}`}
                />
              )}
              {consentMissing && (
                <span
                  className="inline-flex items-center justify-center w-3.5 h-3.5 bg-amber-500 text-white rounded-full ml-0.5 shrink-0 border border-white shadow-sm"
                  title="Einverständnis-Erklärung fehlt (Kryo/EMS) – bitte vor dem Training unterschreiben lassen"
                  data-testid={`consent-missing-badge-${customerId}`}
                >
                  <FileSignature size={9} strokeWidth={2.5} />
                </span>
              )}
            </div>
            {apt.notes && apt.notes.trim() && (
              <div className="absolute -top-0.5 -right-0.5 z-10">
                <span
                  className="w-3.5 h-3.5 bg-red-500 text-white rounded-full flex items-center justify-center text-[8px] font-bold shadow-sm border border-white"
                  title={`Notiz: ${apt.notes}`}
                >
                  !
                </span>
              </div>
            )}
          </div>
        ) : (
          <div
            className="relative w-full h-full"
            style={{ color: getAppointmentTextColor(appointmentColor) }}
          >
            {apt.is_addon && (
              <div
                className="absolute -top-1 -left-1 z-10"
                data-testid={`addon-badge-${apt.id}`}
              >
                <span
                  className="w-5 h-5 bg-purple-600 text-white rounded-full flex items-center justify-center shadow-md border border-white"
                  title="Zusatzleistung (Add-on)"
                >
                  <Snowflake size={12} strokeWidth={2.5} />
                </span>
              </div>
            )}
            {apt.booking_source && (
              <div className="absolute top-0 right-0 opacity-40">
                <BookingSourceIcon source={apt.booking_source} />
              </div>
            )}
            {apt.notes && apt.notes.trim() && (
              <div className="absolute -top-0.5 -right-0.5 z-10">
                <span
                  className="w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center text-[9px] font-bold shadow-sm border border-white"
                  title={`Notiz: ${apt.notes}`}
                >
                  !
                </span>
              </div>
            )}
            <div className="text-[10px] font-semibold text-center">
              {startTimeStr}-{endTimeStr}
            </div>
            <div className="text-[10px] text-center truncate w-full px-0.5">
              {apt.service_name}{apt.selected_variant ? ` (${apt.selected_variant.replace(/\s*Training$/i, '').trim()})` : ''}
            </div>
            {photoUrl && (
              <div className="flex justify-center mt-0.5 mb-0.5">
                <SlotAvatar photoUrl={photoUrl} size={22} />
              </div>
            )}
            <div className="text-[10px] font-semibold text-center truncate w-full px-0.5">
              {apt._isReservation ? (
                <span
                  className="underline decoration-dotted cursor-pointer hover:opacity-80"
                  onClick={goToCustomer}
                  title="Zum Kundenprofil"
                  data-testid={`reservation-customer-link-${apt._reservationId || customerId}`}
                >
                  {name}
                </span>
              ) : (
                name
              )}
              {hasFailedPayment && (
                <span
                  className="inline-block w-1.5 h-1.5 rounded-full bg-red-600 ml-1 align-middle"
                  title="Offene Rechnung – Karte nicht belastbar"
                  data-testid={`failed-payment-dot-compact-${customerId}`}
                />
              )}
              {consentMissing && (
                <span
                  className="inline-flex items-center justify-center w-3 h-3 bg-amber-500 text-white rounded-full ml-1 align-middle border border-white"
                  title="Einverständnis-Erklärung fehlt (Kryo/EMS)"
                  data-testid={`consent-missing-badge-compact-${customerId}`}
                >
                  <FileSignature size={7} strokeWidth={2.5} />
                </span>
              )}
            </div>
            {apt._isReservation && phone && (
              <div
                className="text-[9px] text-center truncate w-full px-0.5 flex items-center justify-center gap-0.5"
                data-testid={`reservation-phone-${apt._reservationId || customerId}`}
              >
                <Phone size={8} strokeWidth={2.5} />
                <span className="truncate">{phone}</span>
              </div>
            )}
          </div>
        )}
        {/* 10/2026 Resize-Griff an der Unterkante — ziehen ändert die Dauer */}
        {canResize && (
          <div
            data-testid={`resize-handle-${apt.id}`}
            draggable={false}
            onMouseDown={startResize}
            onDragStart={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => e.stopPropagation()}
            className="absolute left-0 right-0 bottom-0 flex items-end justify-center cursor-ns-resize"
            style={{ height: '10px', zIndex: 20 }}
            title="Dauer ändern (Unterkante ziehen)"
          >
            <div className="mb-0.5 w-7 h-1 rounded-full bg-white/80 shadow" />
          </div>
        )}
        {resizing && liveEndLabel && (
          <div
            data-testid={`resize-live-${apt.id}`}
            className={`absolute left-1/2 -translate-x-1/2 text-white text-[11px] font-semibold px-2 py-0.5 rounded shadow-xl z-30 whitespace-nowrap pointer-events-none ${
              resizeConflict ? 'bg-red-600' : 'bg-gray-900'
            }`}
            style={{ bottom: '-20px' }}
          >
            {resizeConflict ? `⚠️ ${liveEndLabel} belegt` : liveEndLabel}
          </div>
        )}
        {resizing && resizeConflict && (
          <div
            className="absolute inset-0 rounded pointer-events-none z-20"
            style={{
              border: '3px dashed #ef4444',
              backgroundColor: 'rgba(239, 68, 68, 0.25)',
            }}
          />
        )}
      </div>
    </TooltipTrigger>
    <TooltipContent
      side="top"
      className="bg-gray-900 border-2 border-gray-600 shadow-xl p-3 max-w-xs z-[100] rounded-lg"
      sideOffset={8}
    >
      <div className="space-y-1.5">
        <div className="font-semibold text-white text-sm flex items-center gap-2">
          {photoUrl && <SlotAvatar photoUrl={photoUrl} size={34} />}
          {apt._isReservation && customerId ? (
            <span className="underline decoration-dotted cursor-pointer hover:text-amber-200" onClick={goToCustomer}>
              {name}
            </span>
          ) : (
            name
          )}
          {/* Anamnese Warning - Red ! if no anamnese (nicht bei Reservationen) */}
          {!apt._isReservation && !anamneseStatus[customerId] && (
            <span
              className="flex items-center gap-1 text-red-400 text-xs"
              title="Keine Anamnese vorhanden"
            >
              <AlertCircle className="h-4 w-4" />
            </span>
          )}
        </div>
        {apt._isReservation && (
          <div className="flex items-center gap-1.5 text-amber-300 text-xs font-medium bg-amber-900/40 px-2 py-1 rounded" data-testid="reservation-tooltip-info">
            <span>🔒 Chatbot-Reservation · läuft ab {new Date(apt._expiresAt).toLocaleString('de-CH', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
          </div>
        )}
        {!apt._isReservation && !anamneseStatus[customerId] && (
          <div className="flex items-center gap-1.5 text-red-400 text-xs font-medium bg-red-900/30 px-2 py-1 rounded">
            <AlertCircle className="h-3 w-3" />
            <span>Anamnese fehlt!</span>
          </div>
        )}
        {apt.is_addon && (
          <div className="flex items-center gap-1.5 text-purple-300 text-xs font-medium bg-purple-900/40 px-2 py-1 rounded">
            <Snowflake className="h-3 w-3" />
            <span>Zusatzleistung (Add-on)</span>
          </div>
        )}
        {consentMissing && (
          <div className="flex items-center gap-1.5 text-amber-300 text-xs font-medium bg-amber-900/40 px-2 py-1 rounded" data-testid={`consent-missing-tooltip-${customerId}`}>
            <FileSignature className="h-3 w-3" />
            <span>Einverständnis-Erklärung fehlt — bitte unterschreiben lassen!</span>
          </div>
        )}
        {phone && (
          <div className="flex items-center gap-1.5 text-gray-300 text-xs">
            <Phone className="h-3 w-3" />
            <span>{phone}</span>
          </div>
        )}
        {/* Anzugsgrösse nur bei EMS/Flex anzeigen */}
        {isEmsOrFlex && suitSize && (
          <div className="flex items-center gap-1.5 text-teal-300 text-xs font-medium">
            <span>👕 Anzug:</span>
            <span className="bg-teal-700 px-1.5 py-0.5 rounded">
              {suitSize}
            </span>
          </div>
        )}
        {isEmsOrFlex && !suitSize && (
          <div className="flex items-center gap-1.5 text-orange-400 text-xs">
            <span>👕 Anzugsgrösse nicht hinterlegt</span>
          </div>
        )}
        {/* Eigene Kundenfelder (als "Termin-Block" markiert) */}
        {(blockFields || []).map((bf, i) => (
          <div key={i} className="flex items-center gap-1.5 text-sky-300 text-xs font-medium" data-testid={`block-field-${customerId}-${i}`}>
            <span className="text-sky-200">{bf.label}:</span>
            <span className="bg-sky-800 px-1.5 py-0.5 rounded">{bf.value}</span>
          </div>
        ))}
        <div className="text-gray-300 text-xs">
          <span className="font-medium text-gray-200">Service:</span>{' '}
          {apt.service_name}{apt.selected_variant ? ` (${apt.selected_variant.replace(/\s*Training$/i, '').trim()})` : ''}
        </div>
        <div className="text-gray-300 text-xs">
          <span className="font-medium text-gray-200">Zeit:</span>{' '}
          {startTimeStr} - {endTimeStr}
        </div>
        {apt.notes && apt.notes.trim() && (
          <div className="text-gray-400 text-xs border-t border-gray-600 pt-1.5 mt-1.5">
            <span className="font-medium text-gray-300">Notiz:</span>{' '}
            {apt.notes}
          </div>
        )}
      </div>
    </TooltipContent>
  </Tooltip>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Sub-Slot durch direkt in Mindbody gebuchten Termin gesperrt
// ─────────────────────────────────────────────────────────────────────
export const MindbodyBlockedSlot = ({
  apt,
  slotIdx,
  maxParticipants,
  topPosition,
  leftPosition,
  widthPercentage,
  height,
}) => (
  <div
    key={`${apt.id}-mb-blocked-${slotIdx}`}
    data-testid={`mb-blocked-subslot-${apt.id}-${slotIdx}`}
    title="Platz durch Mindbody-Termin belegt"
    className="absolute flex flex-col items-center justify-center cursor-not-allowed"
    style={{
      top: `${topPosition}px`,
      left: `${leftPosition}%`,
      width: `${widthPercentage}%`,
      height: `${height}px`,
      background:
        'repeating-linear-gradient(45deg, rgba(75,85,99,0.55), rgba(75,85,99,0.55) 8px, rgba(107,114,128,0.55) 8px, rgba(107,114,128,0.55) 16px)',
      zIndex: 10,
      borderTop: '2px solid #4b5563',
      borderRight:
        slotIdx < maxParticipants - 1 ? '1px solid #4b5563' : 'none',
    }}
    onClick={(e) => e.stopPropagation()}
  >
    <span className="text-[10px] font-bold text-white uppercase tracking-wider drop-shadow">
      BELEGT
    </span>
    <span className="text-[9px] text-gray-100">Mindbody</span>
  </div>
);

// ─────────────────────────────────────────────────────────────────────
// Leerer Sub-Slot (Plus + Klick öffnet Buchung, Drop nimmt Teilnehmer auf)
// ─────────────────────────────────────────────────────────────────────
export const EmptyParticipantSlot = ({
  apt,
  slotIdx,
  maxParticipants,
  topPosition,
  leftPosition,
  widthPercentage,
  height,
  startTimeStr,
  movingAppointment,
  onMoveIntoAppointment,
  draggedParticipant,
  setDraggedParticipant,
  setDropTarget,
  services,
  customers,
  fetchAppointments,
  fetchCustomers,
  setSelectedSlot,
  setSelectedTrainer,
  setBookingData,
  setCustomTime,
  setIsAddingParticipant,
  setAvailableServices,
  setBookingDialogOpen,
}) => {
  const isCompatibleDropTarget =
    draggedParticipant &&
    draggedParticipant.serviceId === apt.service_id &&
    draggedParticipant.appointmentId !== apt.id;

  return (
    <div
      key={`${apt.id}-empty-${slotIdx}`}
      className={`absolute p-2 cursor-pointer flex flex-col items-center justify-center transition-all ${
        isCompatibleDropTarget
          ? 'bg-teal-100 border-teal-500 ring-2 ring-teal-400'
          : 'hover:bg-teal-50'
      }`}
      style={{
        top: `${topPosition}px`,
        left: `${leftPosition}%`,
        width: `${widthPercentage}%`,
        height: `${height}px`,
        backgroundColor: isCompatibleDropTarget
          ? 'rgba(20, 184, 166, 0.2)'
          : 'rgba(255,255,255,0.3)',
        zIndex: 10,
        border: isCompatibleDropTarget
          ? '3px solid #14b8a6'
          : '2px dashed #888',
        borderTop: '2px solid #888',
        borderRight:
          slotIdx < maxParticipants - 1 ? '1px solid #888' : 'none',
      }}
      onDragOver={(e) => {
        if (
          draggedParticipant &&
          draggedParticipant.serviceId === apt.service_id &&
          draggedParticipant.appointmentId !== apt.id
        ) {
          e.preventDefault();
          e.stopPropagation();
          e.dataTransfer.dropEffect = 'move';
        }
      }}
      onDrop={async (e) => {
        if (
          draggedParticipant &&
          draggedParticipant.serviceId === apt.service_id &&
          draggedParticipant.appointmentId !== apt.id
        ) {
          e.preventDefault();
          e.stopPropagation();
          try {
            await axios.post(
              `${API}/appointments/${draggedParticipant.appointmentId}/move-participant`,
              {
                customer_id: draggedParticipant.customerId,
                target_trainer_id: apt.trainer_id,
                target_start_time: apt.start_time,
                target_end_time: apt.end_time,
                target_appointment_id: apt.id,
              }
            );
            toast.success(
              `${draggedParticipant.customerName} zu Termin hinzugefügt!`
            );
            setDraggedParticipant(null);
            setDropTarget(null);
            fetchAppointments();
          } catch (error) {
            toast.error(
              error.response?.data?.detail || 'Fehler beim Hinzufügen'
            );
            setDraggedParticipant(null);
            setDropTarget(null);
          }
        }
      }}
      onClick={(e) => {
        e.stopPropagation();
        // 08/2026: Im Verschiebe-Modus → Kunde direkt in DIESEN Termin einbuchen
        if (movingAppointment && onMoveIntoAppointment) {
          onMoveIntoAppointment(apt);
          return;
        }
        // Open booking dialog for this appointment time/service
        setSelectedSlot(startTimeStr);
        setSelectedTrainer({ trainer: { id: apt.trainer_id } });
        setBookingData({
          service_id: apt.service_id,
          customer_id: '',
          notes: '',
        });
        setCustomTime(startTimeStr);
        setIsAddingParticipant(true);

        // Lock to the same service only
        setAvailableServices(services.filter((s) => s.id === apt.service_id));
        if (customers.length === 0) fetchCustomers();

        setBookingDialogOpen(true);
      }}
    >
      <Plus size={24} className="text-gray-500" />
      <div className="text-xs text-gray-600 mt-1">Teilnehmer hinzufügen</div>
    </div>
  );
};
